import type { AxiosInstance } from "axios";
import { Check, Circle, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../components/ui/button.js";
import { Card, CardContent } from "../components/ui/card.js";
import { cn } from "../lib/cn.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchOnboarding, isLive, wizardSteps, type OnboardingStatus, type WizardStep } from "../lib/onboarding.js";

/**
 * The setup wizard a store sees after connecting, until it is fully live.
 *
 * EVIDENCE, NOT CHECKBOXES. Each step is marked done because the server SAW something —
 * events arriving, an order tracked — not because the merchant clicked "I did this". Doc
 * 03 §signup asks for exactly that: the wizard's green check is the Store Health board
 * scoped to one store, so "it says done" and "it actually works" can never disagree.
 *
 * IT POLLS while waiting. Turning on tracking, then a visit, then an order are things that
 * happen on the storefront in the merchant's own time — the wizard watches for them rather
 * than making the merchant refresh.
 */
export function OnboardingScreen({
  client,
  tenantId,
  onDone,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  /** Called once the store is fully live, so the shell can move on to the reports. */
  readonly onDone?: () => void;
}): React.JSX.Element {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await fetchOnboarding(client));
      setError(null);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load setup status."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  // Poll every 8s WHILE NOT LIVE. Stops the moment the store is live.
  //
  // IT NO LONGER FORWARDS TO A REPORT. The old code called onDone() here, so opening Setup
  // on an already-live store teleported you to Source Attribution with no explanation —
  // the "I clicked Setup and landed on a report" confusion. Now a live store renders an
  // explicit "you're all set" panel below, and the nav hides Setup once live anyway.
  useEffect(() => {
    if (status !== null && isLive(status)) return;
    const timer = setInterval(() => void load(), 8_000);
    return () => clearInterval(timer);
  }, [status, load]);

  if (error !== null && status === null) {
    return <p className="text-destructive p-6 text-sm">{error}</p>;
  }
  if (status === null) {
    return <p className="text-muted-foreground p-6 text-sm">Loading…</p>;
  }

  if (isLive(status)) {
    return (
      <div className="mx-auto flex min-w-0 max-w-2xl flex-col gap-4">
        <Card>
          <CardContent className="flex flex-col items-start gap-3 pt-6">
            <span className="flex size-8 items-center justify-center rounded-full bg-emerald-500 text-white">
              <Check className="size-5" />
            </span>
            <div>
              <h1 className="text-lg font-semibold">You&apos;re all set</h1>
              <p className="text-muted-foreground text-sm">
                {status.shopDomain} is live and tracking. Your reports are filling with data.
              </p>
            </div>
            <Button onClick={() => onDone?.()}>View your reports</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const steps = wizardSteps(status);

  return (
    <div className="mx-auto flex min-w-0 max-w-2xl flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Finish setting up {status.shopDomain}</h1>
        <p className="text-muted-foreground text-sm">
          A few steps and your reports fill with live data. This page updates on its own as each one completes.
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-col gap-1 pt-6">
          {steps.map((step, index) => (
            <StepRow
              key={step.key}
              step={step}
              last={index === steps.length - 1}
              status={status}
            />
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="size-4" />
          Check now
        </Button>
        {status.lastEventAt !== null && (
          <span className="text-muted-foreground text-xs">
            Last event {new Date(status.lastEventAt).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

function StepRow({
  step,
  last,
  status,
}: {
  readonly step: WizardStep;
  readonly last: boolean;
  readonly status: OnboardingStatus;
}): React.JSX.Element {
  return (
    <div className={cn("flex gap-3 pb-4", !last && "border-b")}>
      <div className="mt-0.5 shrink-0">
        {step.status === "done" ? (
          <span className="flex size-6 items-center justify-center rounded-full bg-emerald-500 text-white">
            <Check className="size-4" />
          </span>
        ) : step.status === "current" ? (
          <span className="border-primary text-primary flex size-6 items-center justify-center rounded-full border-2">
            <Loader2 className="size-3.5 animate-spin" />
          </span>
        ) : (
          <span className="text-muted-foreground/40 flex size-6 items-center justify-center">
            <Circle className="size-4" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className={cn("text-sm font-medium", step.status === "upcoming" && "text-muted-foreground")}>{step.title}</p>
        <StepDetail step={step} status={status} />
      </div>
    </div>
  );
}

/** The one line under a step — an action to take, or what we are waiting to see. */
function StepDetail({ step, status }: { readonly step: WizardStep; readonly status: OnboardingStatus }): React.JSX.Element | null {
  if (step.status === "upcoming") return null;

  if (step.key === "enable-tracking") {
    if (step.status === "done") {
      return <p className="text-muted-foreground text-xs">Tracking is on ({status.trackerVersion ?? "active"}).</p>;
    }
    return (
      <div className="mt-1">
        <p className="text-muted-foreground text-xs">
          Turn on the Ecomally app embed in your Shopify theme so the tracker loads on your storefront.
        </p>
        {status.enableTrackingUrl !== null && (
          <Button asChild size="sm" className="mt-2">
            <a href={status.enableTrackingUrl} target="_blank" rel="noreferrer">
              Open theme editor
              <ExternalLink className="size-3.5" />
            </a>
          </Button>
        )}
      </div>
    );
  }

  if (step.key === "waiting-for-traffic") {
    return step.status === "done" ? (
      <p className="text-muted-foreground text-xs">{status.eventsSeen.toLocaleString()} events received.</p>
    ) : (
      <p className="text-muted-foreground text-xs">
        Waiting for the first visit. Open your storefront in a new tab to send one.
      </p>
    );
  }

  // waiting-for-order
  return status.firstOrderTracked ? (
    <p className="text-muted-foreground text-xs">An order has been attributed. You are live.</p>
  ) : (
    <p className="text-muted-foreground text-xs">
      Waiting for the first tracked order. Reports fill in as orders arrive.
    </p>
  );
}
